import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal, Pencil, PauseCircle, PlayCircle, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DataTableHeaderRow,
  DataTableTable,
  DataTableTbody,
  DataTableTd,
  DataTableTh,
  DataTableThead,
  DataTableTr,
} from '@/components/ui/data-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type {
  CarePathwayTemplateState,
  CarePathwayTemplateViewModel,
} from '@/features/patient-record/types';

interface AdminCarePathwayTemplatesTableProps {
  readonly templates: readonly CarePathwayTemplateViewModel[];
  readonly busyId: string | null;
  readonly onEdit: (template: CarePathwayTemplateViewModel) => void;
  readonly onToggleState: (template: CarePathwayTemplateViewModel) => void;
  readonly onDelete: (template: CarePathwayTemplateViewModel) => void;
}

export function AdminCarePathwayTemplatesTable({
  templates,
  busyId,
  onEdit,
  onToggleState,
  onDelete,
}: AdminCarePathwayTemplatesTableProps): ReactElement {
  const { t } = useTranslation();

  return (
    <DataTableTable aria-label={t('routes.admin.carePathways.tableRegion')}>
      <DataTableThead>
        <DataTableHeaderRow>
          <DataTableTh widthClassName="w-[28%] min-w-[10rem]">
            {t('routes.admin.carePathways.tableName')}
          </DataTableTh>
          <DataTableTh widthClassName="w-[34%]">
            {t('routes.admin.carePathways.tableDescription')}
          </DataTableTh>
          <DataTableTh className="w-20" align="right">
            {t('routes.admin.carePathways.tableSteps')}
          </DataTableTh>
          <DataTableTh className="w-28">
            {t('routes.admin.carePathways.tableState')}
          </DataTableTh>
          <DataTableTh className="w-28" align="right">
            {t('routes.admin.carePathways.tableEnrollments')}
          </DataTableTh>
          <DataTableTh className="w-10" align="right">
            <span className="sr-only">{t('routes.admin.carePathways.actionsHeader')}</span>
          </DataTableTh>
        </DataTableHeaderRow>
      </DataTableThead>
      <DataTableTbody>
        {templates.map((tpl) => (
          <DataTableTr key={tpl.id}>
            <DataTableTd className="font-medium text-foreground">
              {tpl.name}
            </DataTableTd>
            <DataTableTd className="text-muted-foreground text-xs">
              {tpl.description.length > 0 ? tpl.description : '—'}
            </DataTableTd>
            <DataTableTd className="tabular-nums text-foreground/90" align="right">
              {tpl.stepCount.toLocaleString()}
            </DataTableTd>
            <DataTableTd>
              <StateBadge state={tpl.state} />
            </DataTableTd>
            <DataTableTd className="tabular-nums text-foreground/90" align="right">
              {tpl.enrollmentCount.toLocaleString()}
            </DataTableTd>
            <DataTableTd align="right">
              <DropdownMenu>
                <DropdownMenuTrigger asChild disabled={busyId === tpl.id}>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={t('routes.admin.carePathways.rowActionsAriaLabel', {
                      name: tpl.name,
                    })}
                    className="h-7 w-7 p-0"
                  >
                    <MoreHorizontal
                      className="h-4 w-4 text-muted-foreground"
                      aria-hidden="true"
                    />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[11rem]">
                  <DropdownMenuItem
                    onSelect={() => {
                      onEdit(tpl);
                    }}
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('routes.admin.carePathways.editAction')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => {
                      onToggleState(tpl);
                    }}
                  >
                    {tpl.state === 'ACTIVE' ? (
                      <>
                        <PauseCircle className="h-3.5 w-3.5" aria-hidden="true" />
                        {t('routes.admin.carePathways.disableAction')}
                      </>
                    ) : (
                      <>
                        <PlayCircle className="h-3.5 w-3.5" aria-hidden="true" />
                        {t('routes.admin.carePathways.enableAction')}
                      </>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    disabled={tpl.enrollmentCount > 0}
                    title={
                      tpl.enrollmentCount > 0
                        ? t('routes.admin.carePathways.deleteBlockedTooltip', {
                            count: tpl.enrollmentCount,
                          })
                        : undefined
                    }
                    onSelect={() => {
                      if (tpl.enrollmentCount > 0) return;
                      onDelete(tpl);
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('routes.admin.carePathways.deleteAction')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </DataTableTd>
          </DataTableTr>
        ))}
      </DataTableTbody>
    </DataTableTable>
  );
}

const STATE_STYLES: Record<CarePathwayTemplateState, { bg: string; text: string; dot: string }> = {
  ACTIVE:   { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  DISABLED: { bg: 'bg-muted',      text: 'text-muted-foreground', dot: 'bg-muted-foreground' },
};

function StateBadge({ state }: { readonly state: CarePathwayTemplateState }): ReactElement {
  const { t } = useTranslation();
  const styles = STATE_STYLES[state];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium',
        styles.bg,
        styles.text,
      )}
    >
      <span aria-hidden="true" className={cn('h-1.5 w-1.5 rounded-full', styles.dot)} />
      {t(`routes.admin.carePathways.stateLabel.${state}`)}
    </span>
  );
}
